import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpErrorResponse,
} from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { Strings } from "../classes/messages";
import { catchError, first, switchMap } from "rxjs/operators";
import { AuthenticationService } from "../services/authentication.service";
import { Builder } from "builder-pattern";
import { ToolsService } from "../services/tools.service";
import { StepperService } from "../services/stepper.service";
import { CustomError } from "../classes/custom-error";
import { TraceIdService } from "../services/tools/trace-id.service";
import { environment } from "../../environments/environment";

@Injectable({
    providedIn: "root",
})
export class AuthenticationInterceptorService implements HttpInterceptor {
    private static getTypeErrorByStatus(status: number): string {
        switch (status) {
            case 400:
                return Strings.typeErrorUser;
            case 401:
                return Strings.typeErrorUnauthorized;
            case 404:
                return Strings.typeErrorNotFound;
            case 500:
                return Strings.typeErrorRejected;
            default:
                return Strings.typeErrorUnknown;
        }
    }

    constructor(
        private authSrv: AuthenticationService,
        private toolsSrv: ToolsService,
        private stepperSrv: StepperService,
        private injector: Injector,
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
        // Skip interceptor for translation files to avoid circular dependency
        if (req.url.includes('/assets/i18n/') || req.url.includes('/i18n/')) {
            return next.handle(req);
        }

        return this.authSrv.session.pipe(
            first(),
            switchMap((session) => {
                let request = req;
                const step = this.stepperSrv.stepper$.getValue();

                // Prepare headers object
                const headers: { [key: string]: string } = {
                    "X-Jaak-Kyc-Step": this.parseStepFormat(step),
                    language: this.getCurrentLanguage(),
                };

                // Add W3C Trace Context header if OpenTelemetry is enabled
                const otelConfig = (environment as any).openTelemetry;
                if (otelConfig?.enabled) {
                    // Use lazy injection to avoid circular dependency
                    const traceIdService = this.injector.get(TraceIdService);
                    const traceContext = traceIdService.generateTraceContext(
                        otelConfig.sampling?.tracesSampleRate > 0
                    );
                    // Only add traceparent header if we have a valid trace context (with session trace ID)
                    if (traceContext) {
                        headers['traceparent'] = traceContext.traceparent;
                    }
                }

                if (session) {
                    headers['authorization'] = `Bearer ${session.accessToken}`;
                }

                request = req.clone({
                    setHeaders: headers,
                });

                return next.handle(request).pipe(
                    catchError((error: HttpErrorResponse) => {
                        if (error.status === 403) {
                            this.authSrv.removeUserSession();

                            setTimeout(async () => {
                                this.toolsSrv.goRouteLoginPage();
                                return;
                            }, 100);
                        }

                        if (
                            error.url && (
                                error.url.includes("/blacklist/investigate") ||
                                error.url.includes("/kyc/session")
                            )
                        ) {
                            return throwError(this.formatError(error));
                        }

                        return throwError(this.formatError(error));
                    }),
                );
            }),
        );
    }

    private formatError(error: HttpErrorResponse): CustomError {
        const errorResponse = error?.error ?? error;
        return Builder(CustomError)
            .eventId(errorResponse?.eventId ?? "")
            .errorCode(errorResponse?.errorCode ?? "0")
            .description(errorResponse?.description ?? error.message)
            .message(
                errorResponse?.message ?? "Ha ocurrido un error",
            )
            .statusCode(errorResponse?.statusCode ?? error.status)
            .build();
    }

    private getCurrentLanguage(): string {
        // Get language from localStorage to avoid circular dependency with TranslateService
        try {
            const storedLang = localStorage.getItem('language') || localStorage.getItem('currentLang');
            return storedLang || 'es';
        } catch (e) {
            return 'es';
        }
    }

    private parseStepFormat(step: string): string {
        const format = {
            step1: "0",
            step2: "1",
            step3: "2",
            step4: "3",
            step5: "4",
            step6: "5",
            stepList: "6",
            stepResults: "7",
        };
        const formattedStep = format[step];
        return formattedStep;
    }
}
